/**
 * Cookie consent — single source of truth for whether non-essential
 * cookies / tracking technologies (analytics, etc.) may be used.
 *
 * The consent record itself is "strictly necessary" under ePrivacy (it's
 * the mechanism by which we honor the user's choice), so storing it does
 * not require consent.
 *
 * Boot sequence: main.tsx reads getConsent() synchronously and only
 * initializes analytics if status === 'accepted'. If the user later
 * accepts via the banner, subscribers are notified and analytics is
 * initialized at that point.
 */

const STORAGE_KEY = 'fias_cookie_consent';
const SCHEMA_VERSION = 1;

export type ConsentStatus = 'unset' | 'accepted' | 'essential';

export interface ConsentRecord {
  status: ConsentStatus;
  decidedAt: string | null;
  version: number;
}

const DEFAULT_RECORD: ConsentRecord = {
  status: 'unset',
  decidedAt: null,
  version: SCHEMA_VERSION,
};

type Listener = (record: ConsentRecord) => void;

const listeners = new Set<Listener>();
let cached: ConsentRecord | null = null;

function read(): ConsentRecord {
  if (cached) return cached;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      cached = DEFAULT_RECORD;
      return cached;
    }
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (
      parsed.version !== SCHEMA_VERSION ||
      (parsed.status !== 'accepted' && parsed.status !== 'essential')
    ) {
      // Stale schema or invalid record — treat as unset and re-prompt.
      cached = DEFAULT_RECORD;
      return cached;
    }
    cached = {
      status: parsed.status,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : null,
      version: SCHEMA_VERSION,
    };
    return cached;
  } catch {
    // localStorage may throw under SSR / private mode. Default to unset.
    cached = DEFAULT_RECORD;
    return cached;
  }
}

export function getConsent(): ConsentRecord {
  return read();
}

export function setConsent(status: 'accepted' | 'essential'): void {
  const record: ConsentRecord = {
    status,
    decidedAt: new Date().toISOString(),
    version: SCHEMA_VERSION,
  };
  cached = record;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Best-effort persistence. Listeners still receive the in-memory
    // copy so the current session reflects the user's choice.
  }
  for (const listener of listeners) listener(record);
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Re-open the banner so the user can change their mind. Used by the
 * "Manage Cookie Preferences" button on the Privacy Policy page.
 * Implemented as a custom DOM event so the trigger doesn't need a
 * direct ref to the banner.
 */
export const REOPEN_BANNER_EVENT = 'fias:cookie-consent:reopen';

export function reopenBanner(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(REOPEN_BANNER_EVENT));
}

/** Test-only reset. */
export function _resetConsentForTests(): void {
  cached = null;
  listeners.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
